import { supabase } from '../../integrations/supabase/client';
import type { IClientAgentRepository } from '../interfaces/IClientAgentRepository';
import type { ClientAgentRelationship, CreateRelationshipData, UpdateRelationshipData, CopyrightDisclaimerData } from '../../interfaces/clientAgent.interface';

export class SupabaseClientAgentRepository implements IClientAgentRepository {
  async getAll(filters?: {
    tenant_id?: string;
    status?: string;
  }): Promise<ClientAgentRelationship[]> {
    let query = supabase
      .from('client_agent_relationships')
      .select(`
        *,
        client:clients!client_agent_relationships_client_tenant_id_fkey(id, name, tenant_type),
        agent:clients!client_agent_relationships_agent_tenant_id_fkey(id, name, tenant_type)
      `)
      .order('created_at', { ascending: false });

    if (filters?.tenant_id) {
      query = query.or(`client_tenant_id.eq.${filters.tenant_id},agent_tenant_id.eq.${filters.tenant_id}`);
    }

    if (filters?.status) {
      query = query.eq('status', filters.status);
    }

    const { data, error } = await query;
    if (error) throw error;
    return (data || []) as unknown as ClientAgentRelationship[];
  }

  async getById(id: string): Promise<ClientAgentRelationship | null> {
    const { data, error } = await supabase
      .from('client_agent_relationships')
      .select(`
        *,
        client:clients!client_agent_relationships_client_tenant_id_fkey(id, name, tenant_type),
        agent:clients!client_agent_relationships_agent_tenant_id_fkey(id, name, tenant_type)
      `)
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as unknown as ClientAgentRelationship;
  }

  async create(data: CreateRelationshipData): Promise<ClientAgentRelationship> {
    const { data: created, error } = await (supabase
      .from('client_agent_relationships') as any)
        .insert(data)
      .select(`
        *,
        client:clients!client_agent_relationships_client_tenant_id_fkey(id, name, tenant_type),
        agent:clients!client_agent_relationships_agent_tenant_id_fkey(id, name, tenant_type)
      `)
      .single();

    if (error) throw error;
    return created as unknown as ClientAgentRelationship;
  }

  async update(id: string, data: UpdateRelationshipData): Promise<ClientAgentRelationship> {
    const { data: updated, error } = await supabase
      .from('client_agent_relationships')
      .update(data)
      .eq('id', id)
      .select(`
        *,
        client:clients!client_agent_relationships_client_tenant_id_fkey(id, name, tenant_type),
        agent:clients!client_agent_relationships_agent_tenant_id_fkey(id, name, tenant_type)
      `)
      .single();

    if (error) throw error;
    return updated as unknown as ClientAgentRelationship;
  }

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('client_agent_relationships')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  async getAgentsForClient(clientTenantId: string): Promise<ClientAgentRelationship[]> {
    const { data, error } = await (supabase.rpc as any)('get_client_agents', { p_client_id: clientTenantId });

    if (error) throw error;
    return (data || []) as unknown as ClientAgentRelationship[];
  }

  async getClientsForAgent(agentTenantId: string): Promise<ClientAgentRelationship[]> {
    const { data, error } = await (supabase.rpc as any)('get_agent_clients', { agent_id: agentTenantId });

    if (error) throw error;
    return (data || []) as unknown as ClientAgentRelationship[];
  }

  async signCopyrightDisclaimer(data: CopyrightDisclaimerData): Promise<void> {
    const { error } = await supabase
      .from('client_agent_relationships')
      .update({
        copyright_disclaimer_signed: true,
        copyright_disclaimer_signed_at: new Date().toISOString(),
        copyright_disclaimer_signed_by: data.signed_by_user_id,
        copyright_disclaimer_content: data.disclaimer_content
      })
      .eq('id', data.relationship_id);

    if (error) throw error;
  }

  async isCopyrightDisclaimerRequired(relationshipId: string): Promise<boolean> {
    const { data, error } = await supabase
      .rpc('copyright_disclaimer_required', { relationship_id: relationshipId });

    if (error) throw error;
    return data || false;
  }
}
